import { Alert, Box, type Breakpoint, TextField } from '@mui/material';
import { type ReactNode, useState } from 'react';

import { useUserPermissions } from '@hooks/useUserPermissions';
import { Roles } from '@rdTypes/shared';
import { DialogConfirmation } from '@uiKit/DialogConfirmation';
import NotifyMailroomSwitch from 'src/components/NotifyMailroomSwitch';

import { CollapsibleOptions } from './CollapsibleOptions';

type Step = 'info' | 'action';

interface ActionWithInfoChildProps {
  step: Step;
  skipNotifyRecipient: boolean;
  comment: string;
  notifyMailroom: boolean;
  onClose: () => void;
  onBackToInfo: (warning?: string) => void;
}

interface ActionWithInfoProps {
  open: boolean;
  title: string;
  infoContent: ReactNode;
  onClose: () => void;
  maxWidth?: Breakpoint;
  acceptButtonTitle?: string;
  cancelButtonTitle?: string;
  loading?: boolean;
  onAccept?: (state: { skipNotifyRecipient: boolean; comment: string; notifyMailroom: boolean }) => Promise<void>;
  children: (props: ActionWithInfoChildProps) => ReactNode;
}

export const ActionWithInfo = ({
  open,
  title,
  infoContent,
  onClose,
  maxWidth = 'sm',
  acceptButtonTitle = 'Continue',
  cancelButtonTitle = 'Cancel',
  loading,
  onAccept,
  children,
}: ActionWithInfoProps) => {
  const { isAllowed } = useUserPermissions();
  const isAdmin = isAllowed([Roles.Admin]);

  const [step, setStep] = useState<Step>('info');
  const [skipNotifyRecipient, setSkipNotifyRecipient] = useState(false);
  const [comment, setComment] = useState('');
  const [notifyMailroom, setNotifyMailroom] = useState(true);
  const [warning, setWarning] = useState<string | null>(null);

  const handleClose = () => {
    setStep('info');
    setSkipNotifyRecipient(false);
    setComment('');
    setNotifyMailroom(true);
    setWarning(null);
    onClose();
  };

  const handleAccept = async () => {
    if (onAccept) {
      await onAccept({ skipNotifyRecipient, comment, notifyMailroom });
    } else {
      setWarning(null);
      setStep('action');
    }
  };

  const handleBackToInfo = (warningMessage?: string) => {
    setStep('info');
    setWarning(warningMessage || null);
  };

  return (
    <>
      <DialogConfirmation
        open={open && step === 'info'}
        maxWidth={maxWidth}
        title={title}
        content={
          <Box>
            {infoContent}
            {warning && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {warning}
              </Alert>
            )}

            <CollapsibleOptions>
              <TextField
                sx={{ width: '100%', mt: 1, mb: 2 }}
                label="Optional Comment"
                multiline
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <NotifyMailroomSwitch
                checked={notifyMailroom}
                onChange={() => setNotifyMailroom(!notifyMailroom)}
                label="Notify mailroom users about comment"
              />
              {isAdmin && (
                <Box sx={{ mt: 1 }}>
                  <NotifyMailroomSwitch
                    checked={skipNotifyRecipient}
                    onChange={() => setSkipNotifyRecipient(!skipNotifyRecipient)}
                    label="Skip notification to recipient about completed request"
                  />
                </Box>
              )}
            </CollapsibleOptions>
          </Box>
        }
        acceptButtonHandleClick={handleAccept}
        loading={loading}
        cancelButtonHandleClick={handleClose}
        acceptButtonTitle={acceptButtonTitle}
        cancelButtonTitle={cancelButtonTitle}
      />

      {children({
        step,
        skipNotifyRecipient,
        comment,
        notifyMailroom,
        onClose: handleClose,
        onBackToInfo: handleBackToInfo,
      })}
    </>
  );
};
